import React, { useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { Callout } from '../OrderProcessParts';
import type { OrderDocumentLanguage } from '../../api/ordersApi';

export type OfferPdfPreviewStatus = 'loading' | 'ready' | 'error';

type PreviewState =
  | { status: 'loading' }
  | { status: 'ready'; url: string }
  | { status: 'error' };

/**
 * Makieta `sOferta`, „Podgląd oferty”: the very PDF of the „Pobierz ofertę
 * (PDF)” button, shown in the card. The blob comes from the page's existing
 * order-PDF download (same endpoint, same role gate, same sale-price
 * redaction) and is shown in an `<iframe>` through an object URL. It reloads
 * after a language change and whenever `refreshKey` changes (lines, prices,
 * order update); an error leaves the table of lines and a hint to download.
 */
export default function OfferPdfPreview({
  load,
  language,
  refreshKey,
  onStatusChange,
}: {
  load: (language: OrderDocumentLanguage) => Promise<Blob>;
  language: OrderDocumentLanguage;
  /** Anything that changes the document (lines, prices, last update of the order). */
  refreshKey: string;
  onStatusChange?: (status: OfferPdfPreviewStatus) => void;
}) {
  const { t } = useTranslation(['orders']);
  const [state, setState] = useState<PreviewState>({ status: 'loading' });
  // The page passes an inline function: the latest one is read at load time only.
  const loadRef = useRef(load);
  loadRef.current = load;
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ status: 'loading' });
    loadRef.current(language)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setState({ status: 'ready', url: objectUrl });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [language, refreshKey]);

  useEffect(() => { onStatusChangeRef.current?.(state.status); }, [state.status]);

  return (
    <div className="zk-offer-pdf" data-testid="zk-offer-pdf" data-state={state.status}>
      {state.status === 'loading' && (
        <div className="zk-offer-pdf__loading" role="status">
          <Spin tip={t('orders:process.offer.previewLoading')}><div style={{ padding: 40 }} /></Spin>
        </div>
      )}
      {state.status === 'ready' && (
        <iframe
          className="zk-offer-pdf__frame"
          src={`${state.url}#toolbar=0&navpanes=0`}
          title={t('orders:process.offer.previewFrameTitle')}
          data-testid="zk-offer-pdf-frame"
        />
      )}
      {state.status === 'error' && (
        <Callout tone="warning" testId="zk-offer-pdf-error">{t('orders:process.offer.previewError')}</Callout>
      )}
    </div>
  );
}
